import { AxiosResponse } from 'axios';
import CalendarDto, { CalendarStatus } from 'common/dto-types/calendar-dto';
import ColorDto from 'common/dto-types/color-dto';
import EventDto, { EventLogDto } from 'common/dto-types/event-dto';
import ListWithTotal from 'common/dto-types/list-with-total-dto';
import SynchronizeEventsResponseDto from 'common/dto-types/synchronize-events-repsonse-dto';
import TranslationLanguageDto from 'common/dto-types/translation-language-dto';
import axiosServices, { PaginationAndFiltersParams, buildQueryParams } from 'utils/axios';
import { TranslationDataDto } from '../../../common/dto-types/translation-language-dto';

const GET_CALENDARS_URL = 'calendars';
const CREATE_CALENDAR_URL = 'calendars';
const UPDATE_CALENDAR_URL = 'calendars';
const DELETE_CALENDAR_LOGS_URL = 'calendars/logs';
const GET_TRANSLATIONLANGUAGES_URL = 'translationLanguages';
const POST_TRANSLATEWITHDEEPL_URL = 'translationLanguages/translate';

const GET_CALENDAR_WITH_EVENTS_URL = 'events';
const CREATE_EVENT_URL = 'events';
const UPDATE_EVENT_URL = 'events';
const DELETE_EVENT_URL = 'events';
const CLONE_EVENT_URL = 'events/clonedEvent';
const IMPORT_FDCP_MATCH = 'events/synchronizeFdcpMatch';
const GET_CALENDAR_HISTORY = 'events/logs-list';
const GET_EVENT_FIELD_TEMPLATES = 'events/templates';

const GET_COLORS_URL = 'colors';
export const GET_TEAMS_URL = 'teams';
export const GET_VENUES_URL = 'venues';
export const GET_APPOINTMENT_TYPES_URL = 'appointmentTypes';
export const GET_CALENDARS_FOR_SHARED_EVENT_URL = 'calendars/sharedCalendars';
export const GET_HOST_CITIES = 'cities';

export const fetchCalendarList = async (
    params: PaginationAndFiltersParams,
    status: CalendarStatus,
): Promise<ListWithTotal<CalendarDto> | null | undefined> => {
    try {
        const res = await axiosServices.get(`/${GET_CALENDARS_URL}${buildQueryParams(params)}&status=${status}`);
        return res.data;
    } catch (err) {
        // TODO: Implement a proper way to handle errors
        // https://fifadigital.atlassian.net/browse/FMH-2075
        console.error(err);
        return null;
    }
};

export const createCalendar = async (calendar: CalendarDto): Promise<AxiosResponse> => {
    return await axiosServices
        .post(CREATE_CALENDAR_URL, calendar)
        .then((res) => res)
        .catch((res) => res);
};

export const updateCalendar = async (calendar: CalendarDto): Promise<AxiosResponse> => {
    return await axiosServices
        .put(UPDATE_CALENDAR_URL, calendar)
        .then((res) => res)
        .catch((res) => res);
};

export const changeCalendarStatus = async (calendarId: number, newStatus: CalendarStatus, clearLogs: boolean) => {
    return await axiosServices
        .put(`/${GET_CALENDARS_URL}/${calendarId}/status?clearLogs=${clearLogs}`, newStatus)
        .then((res) => res)
        .catch((res) => res);
};

export const fetchCalendarWithEvents = async (calendarId: number): Promise<CalendarDto | null> => {
    try {
        const res = await axiosServices.get(`/${GET_CALENDAR_WITH_EVENTS_URL}/${calendarId}`);
        return res.data;
    } catch (err) {
        // TODO: Implement a proper way to handle errors
        // https://fifadigital.atlassian.net/browse/FMH-2075
        console.error(err);
        return null;
    }
};

export const fetchCalendarDetails = async (calendarId: number): Promise<CalendarDto | null> => {
    try {
        const res = await axiosServices.get(`/${GET_CALENDARS_URL}/${calendarId}/details`);
        return res.data;
    } catch (err) {
        // TODO: Implement a proper way to handle errors
        // https://fifadigital.atlassian.net/browse/FMH-2075
        console.error(err);
        return null;
    }
};

export const fetchColors = async (): Promise<Array<ColorDto>> => {
    try {
        const res = await axiosServices.get(`/${GET_COLORS_URL}`);
        return res.data;
    } catch (err) {
        // TODO: Implement a proper way to handle errors
        // https://fifadigital.atlassian.net/browse/FMH-2075
        console.error(err);
        return [];
    }
};

export const createEvent = async (event: EventDto): Promise<EventDto | null | undefined> => {
    try {
        return await axiosServices.post(CREATE_EVENT_URL, event).then((res) => res.data);
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const updateEvent = async (event: EventDto): Promise<EventDto | null | undefined> => {
    try {
        return await axiosServices.put(UPDATE_EVENT_URL, event).then((res) => res.data);
    } catch (err: any) {
        console.error(err);
        if (err.status === 409) {
            throw err;
        }

        return null;
    }
};

export const deleteEvent = async (eventId: number): Promise<number | null> => {
    try {
        return await axiosServices.delete(`${DELETE_EVENT_URL}/${eventId}`);
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const cloneEvent = async (eventId: number) => {
    try {
        return await axiosServices.post(`${CLONE_EVENT_URL}/${eventId}`);
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const fetchCalendarVenues = async (params: PaginationAndFiltersParams, calendarId: number) => {
    try {
        const res = await axiosServices.get(`${GET_CALENDARS_URL}/${calendarId}/venues${buildQueryParams(params)}`);
        return res.data;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const fetchCalendarVenuesRaw = async (calendarId: number) => {
    try {
        const res = await axiosServices.get(`${GET_VENUES_URL}?calendarId=${calendarId}`);
        return res.data;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const fetchCalendarTeams = async (params: PaginationAndFiltersParams, calendarId: number) => {
    try {
        const res = await axiosServices.get(`${GET_CALENDARS_URL}/${calendarId}/teams${buildQueryParams(params)}`);
        return res.data;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const fetchCalendarTeamsRaw = async (calendarId: number, languages: string) => {
    try {
        const res = await axiosServices.get(`${GET_TEAMS_URL}?calendarId=${calendarId}&locale=${languages}`);
        return res.data;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const fetchEventFieldTemplates = async (appointmentTypeId: number, languages: string) => {
    try {
        const res = await axiosServices.get(`${GET_EVENT_FIELD_TEMPLATES}/${appointmentTypeId}?locale=${languages}`);
        return res.data;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const addVenuesToCalendar = async (calendarId: number, venueId: number) => {
    try {
        return await axiosServices.post(`${GET_CALENDARS_URL}/${calendarId}/venues?venueIds=${venueId}`);
    } catch (err) {
        console.error(err);
        return null;
    }
};
export const removeVenuesFromCalendar = async (calendarId: number, venueId: number) => {
    try {
        return await axiosServices.delete(`${GET_CALENDARS_URL}/${calendarId}/venues?venueIds=${venueId}`);
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const addTeamsToCalendar = async (calendarId: number, teamId: number) => {
    try {
        return await axiosServices.post(`${GET_CALENDARS_URL}/${calendarId}/teams?teamIds=${teamId}`);
    } catch (err) {
        console.error(err);
        return null;
    }
};
export const removeTeamsFromCalendar = async (calendarId: number, teamId: number) => {
    try {
        return await axiosServices.delete(`${GET_CALENDARS_URL}/${calendarId}/teams?teamIds=${teamId}`);
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const modifySubscriptionVenueFromCalendar = async (
    calendarId: number,
    venueId: number,
    subscriptionValue: boolean,
) => {
    try {
        return await axiosServices.post(
            `${GET_CALENDARS_URL}/${calendarId}/venues/subscriptionConfig?venueId=${venueId}&subscriptionValue=${subscriptionValue}`,
        );
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const synchronizeFdcpMatches = async (
    calendarId: number,
    tournamentFdcpId: string,
): Promise<AxiosResponse<SynchronizeEventsResponseDto> | null | undefined> => {
    try {
        return await axiosServices.post(`${IMPORT_FDCP_MATCH}/${calendarId}/${tournamentFdcpId}`);
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const fetchCalendarHistoryList = async (
    params: PaginationAndFiltersParams,
    calendarId: number,
): Promise<ListWithTotal<EventLogDto> | null | undefined> => {
    try {
        const res = await axiosServices.get(`/${GET_CALENDAR_HISTORY}/${calendarId}${buildQueryParams(params)}`);
        return res.data;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const deleteCalendarLogs = async (calendarId: number): Promise<number | null> => {
    try {
        return await axiosServices.delete(`${DELETE_CALENDAR_LOGS_URL}/${calendarId}`);
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const fetchTranslationLanguagesList = async (): Promise<Array<TranslationLanguageDto> | null> => {
    try {
        const res = await axiosServices.get(`/${GET_TRANSLATIONLANGUAGES_URL}`);
        return res.data;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const translateWithDeepl = async (sourceText: TranslationDataDto): Promise<Array<TranslationDataDto> | null> => {
    try {
        const res = await axiosServices.post(`${POST_TRANSLATEWITHDEEPL_URL}`, sourceText);
        return res.data;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const fetchCalendarHostCities = async (calendarId: number) => {
    try {
        const res = await axiosServices.get(`${GET_HOST_CITIES}?calendarIds=${calendarId}`);
        return res.data;
    } catch (err) {
        console.error(err);
        return null;
    }
};
